/**
 * Activity execution helpers (AUTH55 Slice A).
 * Backend remains authority for transitions, CAS, and permissions.
 * Domain matrix is source-backed — no skip-confirm / restart / undo.
 */

export const ACTIVITY_EXECUTION_STATUSES = Object.freeze([
  'PLANNED',
  'CONFIRMED',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
]);

export const ACTIVITY_EXECUTION_ACTIONS = Object.freeze({
  CONFIRM: 'CONFIRM',
  START: 'START',
  COMPLETE: 'COMPLETE',
  CANCEL: 'CANCEL',
});

/** Journey lifecycles where planning activity mutations are domain-writable. */
export const ACTIVITY_EXECUTION_DOMAIN_WRITABLE_LIFECYCLES = Object.freeze([
  'DRAFT',
  'READY',
  'TRAVELING',
]);

const ACTIONS_BY_STATUS = Object.freeze({
  PLANNED: Object.freeze([ACTIVITY_EXECUTION_ACTIONS.CONFIRM, ACTIVITY_EXECUTION_ACTIONS.CANCEL]),
  CONFIRMED: Object.freeze([ACTIVITY_EXECUTION_ACTIONS.START, ACTIVITY_EXECUTION_ACTIONS.CANCEL]),
  IN_PROGRESS: Object.freeze([ACTIVITY_EXECUTION_ACTIONS.COMPLETE, ACTIVITY_EXECUTION_ACTIONS.CANCEL]),
  COMPLETED: Object.freeze([]),
  CANCELLED: Object.freeze([]),
});

/**
 * Status-only action matrix (domain). Does not encode journey lifecycle or permission.
 */
export function getAllowedActivityExecutionActions(activityStatus) {
  const key = String(activityStatus || '');
  if (!Object.prototype.hasOwnProperty.call(ACTIONS_BY_STATUS, key)) {
    return [];
  }
  return [...ACTIONS_BY_STATUS[key]];
}

/**
 * Backend writableLifecycle for activity confirm/start/complete/cancel.
 * Product Today UX may still de-emphasize DRAFT — that is Slice B composition, not a fake BE rule.
 */
export function isActivityExecutionDomainWritable(journeyLifecycle) {
  return ACTIVITY_EXECUTION_DOMAIN_WRITABLE_LIFECYCLES.includes(String(journeyLifecycle || ''));
}

/**
 * Permission mirror of PlanningPermissionPolicy.requireConfirm/Start/Complete/Cancel.
 * OWNER/COORDINATOR: any activity.
 * MEMBER: only when actorMemberId === createdByMemberId.
 * Returns false when required identity fields are missing (fail closed for MEMBER gating).
 */
export function canActorExecuteActivity({
  requesterRole,
  actorMemberId,
  createdByMemberId,
} = {}) {
  const role = String(requesterRole || '');
  if (role === 'OWNER' || role === 'COORDINATOR') {
    return true;
  }
  if (role !== 'MEMBER') {
    return false;
  }
  const actor = actorMemberId == null || actorMemberId === '' ? '' : String(actorMemberId);
  const creator = createdByMemberId == null || createdByMemberId === '' ? '' : String(createdByMemberId);
  if (!actor || !creator) {
    return false;
  }
  return actor === creator;
}

/**
 * Compose status + journey domain writable + optional permission.
 * When includePermission is false, only status + lifecycle are applied (server remains authority).
 */
export function resolveActivityExecutionCapabilities({
  activityStatus,
  journeyLifecycle,
  requesterRole,
  actorMemberId,
  createdByMemberId,
  includePermission = true,
} = {}) {
  const domainWritable = isActivityExecutionDomainWritable(journeyLifecycle);
  const statusActions = getAllowedActivityExecutionActions(activityStatus);
  if (!domainWritable) {
    return {
      domainWritable: false,
      permitted: includePermission
        ? canActorExecuteActivity({ requesterRole, actorMemberId, createdByMemberId })
        : null,
      actions: [],
    };
  }
  const permitted = includePermission
    ? canActorExecuteActivity({ requesterRole, actorMemberId, createdByMemberId })
    : true;
  return {
    domainWritable: true,
    permitted: includePermission ? permitted : null,
    actions: permitted ? statusActions : [],
  };
}

/**
 * Preserve execution mutation response fields for Slice B immediate feedback.
 * rowVersion is the new itinerary aggregate version (not journey / not activity-local).
 */
export function normalizeActivityExecutionResponse(value) {
  if (!value || typeof value !== 'object') {
    return {
      activityId: '',
      journeyId: '',
      status: '',
      rowVersion: null,
      confirmedAt: null,
      startedAt: null,
      completedAt: null,
      cancelledAt: null,
      cancellationReason: null,
    };
  }
  return {
    activityId: value.activityId == null ? '' : String(value.activityId),
    journeyId: value.journeyId == null ? '' : String(value.journeyId),
    status: value.status == null ? '' : String(value.status),
    rowVersion: value.rowVersion ?? null,
    confirmedAt: value.confirmedAt ?? null,
    startedAt: value.startedAt ?? null,
    completedAt: value.completedAt ?? null,
    cancelledAt: value.cancelledAt ?? null,
    cancellationReason: value.cancellationReason ?? null,
  };
}

/**
 * Classify planning execution errors without swallowing 409.
 * Does not retry or rewrite success.
 */
export function classifyActivityExecutionError(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || cause?.message || '');
  if (code === 90003 || code === 90005 || code === 90006
      || key.includes('token.not.found') || key.includes('token.illegal') || key.includes('token.expired')) {
    return 'unauthorized';
  }
  if (code === 93002 || key === 'travel.planning.permission.denied' || key.includes('permission.denied')) {
    return 'forbidden';
  }
  if (code === 92001 || code === 93001
      || key.includes('not.found')) {
    return 'not-found';
  }
  if (code === 93003 || key === 'travel.planning.conflict' || key.includes('conflict')
      || key.includes('lifecycle')) {
    return 'conflict';
  }
  if (code === 93000 || key.includes('validation')) {
    return 'validation';
  }
  return 'error';
}
